import { memo, useMemo } from "react";
import { Box, SxProps, Theme } from "@mui/material";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

const remarkPlugins = [remarkGfm];

type PromptMarkdownProps = {
  text: string;
  /** Extra styling merged over the defaults (colour, weight, margins). */
  sx?: SxProps<Theme>;
};

/**
 * Markdown body for interactive prompt cards (selection, file upload).
 *
 * Agents author these asks in markdown — a bold label naming the row, a blank
 * line, then the document being requested. Rendering that inside a plain
 * `<Typography>` printed the literal `**Characteristic #12 — Seismic
 * Qualification:**` and collapsed every newline into one run-on line, which
 * made a multi-row batch of asks hard to tell apart.
 *
 * Sized to match the `body2` the cards used before. Block margins are trimmed
 * so the card keeps its own padding: paragraphs get a small gap, and the last
 * block has none.
 */
const PromptMarkdown = memo(({ text, sx }: PromptMarkdownProps) => {
  const baseSx = useMemo<SxProps<Theme>>(
    () => ({
      fontSize: "0.875rem",
      lineHeight: 1.43,
      wordWrap: "break-word",
      "& p": { m: 0, mb: 1 },
      "& ul, & ol": { pl: 2.5, my: 0.5 },
      "& li": { mb: 0.25 },
      "& code": {
        px: 0.5,
        borderRadius: 0.5,
        bgcolor: "action.hover",
        fontSize: "0.8125rem",
      },
      "& strong": { fontWeight: 600 },
      "& > *:last-child": { mb: 0 },
    }),
    [],
  );

  return (
    <Box sx={[baseSx, ...(Array.isArray(sx) ? sx : [sx])] as SxProps<Theme>}>
      <Markdown remarkPlugins={remarkPlugins}>{text}</Markdown>
    </Box>
  );
});

PromptMarkdown.displayName = "PromptMarkdown";

export default PromptMarkdown;
